// src/pages/admin/AdminParts.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash } from "react-icons/fa";
import {
  Eye,
  Menu,
  PackageSearch,
  Search,
  X,
} from "lucide-react";
import AddPartForm from "./AddPartsForm";
import Sidebar from "../../components/Sidebar";
import { API_BASE_URL } from "../../config/api";
/* =========================================================
   HELPERS
========================================================= */
const formatPrice = (value) =>
  new Intl.NumberFormat("sq-AL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
const getPartProfit = (part) =>
  Math.max(0, Number(part?.price || 0) - Number(part?.purchase_price || 0));
const getPartBonus = (part) =>
  (getPartProfit(part) * Number(part?.bonus_share_percent || 0)) / 100;
const getPartImageUrl = (image) => {
  if (!image) {
    return null;
  }
  if (typeof image !== "string") {
    return null;
  }
  const cleanImage = image.trim();
  if (!cleanImage) {
    return null;
  }
  if (
    cleanImage.startsWith("http\://") ||
    cleanImage.startsWith("https\://")
  ) {
    return cleanImage;
  }
  if (cleanImage.startsWith("/uploads/")) {
    return `${API_BASE_URL}${cleanImage}`;
  }
  if (cleanImage.startsWith("uploads/")) {
    return `${API_BASE_URL}/${cleanImage}`;
  }
  return `${API_BASE_URL}/uploads/${cleanImage}`;
};
const initialFilters = {
  search: "",
  category: "All",
  device: "All",
  brand: "All",
  stock: "All",
  minPrice: "",
  maxPrice: "",
};
/* =========================================================
   COMPONENT
========================================================= */
const AdminParts = () => {
  const navigate = useNavigate();
  const dialogRef = useRef(null);
  const formRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] =
    useState(false);
  const [mobileDetailPart, setMobileDetailPart] =
    useState(null);
  const [parts, setParts] = useState([]);
  const [editingPart, setEditingPart] =
    useState(null);
  const [filters, setFilters] =
    useState(initialFilters);
  const [currentPage, setCurrentPage] =
    useState(1);
  const [loading, setLoading] =
    useState(false);
  const [saving, setSaving] =
    useState(false);
  const [message, setMessage] = useState({
    type: "",
    text: "",
  });
  const itemsPerPage = 10;
  /* =====================================================
     FETCH PARTS
  ===================================================== */
  const fetchParts = useCallback(async () => {
    const token = localStorage.getItem(
      "authToken"
    );
    if (!token) {
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/parts`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response
        .json()
        .catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          data?.details ||
          data?.error ||
          data?.message ||
          "Pjesët nuk mund të ngarkoheshin."
        );
      }
      setParts(
        Array.isArray(data)
          ? data
          : data.parts || data.data || []
      );
    } catch (error) {
      console.error(
        "FETCH PARTS ERROR:",
        error
      );
      setMessage({
        type: "error",
        text:
          error.message ||
          "Pjesët nuk mund të ngarkoheshin.",
      });
    } finally {
      setLoading(false);
    }
  }, [navigate]);
  useEffect(() => {
    const storedUser =
      localStorage.getItem("user");
    if (!storedUser) {
      navigate("/login");
      return;
    }
    fetchParts();
  }, [fetchParts, navigate]);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);
  /* =====================================================
     FILTER OPTIONS
  ===================================================== */
  const categories = useMemo(
    () =>
      [
        ...new Set(
          parts
            .map((part) => part.category)
            .filter(Boolean)
        ),
      ].sort(),
    [parts]
  );
  const devices = useMemo(
    () =>
      [
        ...new Set(
          parts
            .map((part) => part.device)
            .filter(Boolean)
        ),
      ].sort(),
    [parts]
  );
  const brands = useMemo(
    () =>
      [
        ...new Set(
          parts
            .map((part) => part.brand)
            .filter(Boolean)
        ),
      ].sort(),
    [parts]
  );
  /* =====================================================
     FILTER PARTS
  ===================================================== */
  const filteredParts = useMemo(() => {
    const searchText = filters.search
      .trim()
      .toLowerCase();
    return parts.filter((part) => {
      const price = Number(
        part.price || 0
      );
      const stock = Number(
        part.stock || 0
      );
      const searchableText = [
        part.id,
        part.name,
        part.category,
        part.device,
        part.brand,
        part.model,
        part.description,
        part.created_by_name,
      ]
        .filter(
          (value) =>
            value !== undefined &&
            value !== null
        )
        .join(" ")
        .toLowerCase();
      if (
        searchText &&
        !searchableText.includes(searchText)
      ) {
        return false;
      }
      if (
        filters.category !== "All" &&
        part.category !== filters.category
      ) {
        return false;
      }
      if (
        filters.device !== "All" &&
        part.device !== filters.device
      ) {
        return false;
      }
      if (
        filters.brand !== "All" &&
        part.brand !== filters.brand
      ) {
        return false;
      }
      if (
        filters.stock === "in" &&
        stock <= 0
      ) {
        return false;
      }
      if (
        filters.stock === "out" &&
        stock > 0
      ) {
        return false;
      }
      if (
        filters.minPrice !== "" &&
        price < Number(filters.minPrice)
      ) {
        return false;
      }
      if (
        filters.maxPrice !== "" &&
        price > Number(filters.maxPrice)
      ) {
        return false;
      }
      return true;
    });
  }, [parts, filters]);
  /* =====================================================
     PAGINATION
  ===================================================== */
  const totalPages = Math.max(
    1,
    Math.ceil(
      filteredParts.length /
      itemsPerPage
    )
  );
  const safePage = Math.min(
    currentPage,
    totalPages
  );
  const displayedParts =
    filteredParts
      .slice(
        (safePage - 1) * itemsPerPage,
        safePage * itemsPerPage
      )
      .map((part) => ({
        ...part,
        imageUrl: getPartImageUrl(
          part.image
        ),
      }));
  const goToPage = (page) => {
    if (
      page < 1 ||
      page > totalPages
    ) {
      return;
    }
    setCurrentPage(page);
  };
  /* =====================================================
     MODAL
  ===================================================== */
  const openAdd = () => {
    setEditingPart(null);
    setMessage({
      type: "",
      text: "",
    });
    formRef.current?.resetForm();
    dialogRef.current?.showModal();
  };
  const openEdit = (part) => {
    setEditingPart(part);
    setMessage({
      type: "",
      text: "",
    });
    window.setTimeout(() => {
      dialogRef.current?.showModal();
    }, 0);
  };
  const closeDialog = () => {
    if (saving) {
      return;
    }
    dialogRef.current?.close();
    formRef.current?.resetForm();
    setEditingPart(null);
  };
  /* =====================================================
     SAVE PART
  ===================================================== */
  const handleSavePart = async (
    partFormData
  ) => {
    const token = localStorage.getItem(
      "authToken"
    );
    if (!token) {
      const error = new Error(
        "Sesioni ka përfunduar. Hyr përsëri."
      );
      setMessage({
        type: "error",
        text: error.message,
      });
      throw error;
    }
    const isEditing = Boolean(
      editingPart?.id
    );
    const url = isEditing
      ? `${API_BASE_URL}/api/parts/${editingPart.id}`
      : `${API_BASE_URL}/api/parts`;
    const method = isEditing
      ? "PUT"
      : "POST";
    setSaving(true);
    setMessage({
      type: "",
      text: "",
    });
    try {
      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: partFormData,
      });
      const responseText =
        await response.text();
      let data = {};
      try {
        data = responseText
          ? JSON.parse(responseText)
          : {};
      } catch {
        data = {
          error:
            responseText ||
            "Përgjigje e pavlefshme nga serveri.",
        };
      }
      if (!response.ok) {
        throw new Error(
          data?.details ||
          data?.sqlMessage ||
          data?.error ||
          data?.message ||
          `Ruajtja dështoi (${response.status})`
        );
      }
      await fetchParts();
      dialogRef.current?.close();
      formRef.current?.resetForm();
      setEditingPart(null);
      setMessage({
        type: "success",
        text: isEditing
          ? "Pjesa u përditësua me sukses."
          : "Pjesa u shtua me sukses.",
      });
      return data;
    } catch (error) {
      console.error(
        "SAVE PART ERROR:",
        error
      );
      setMessage({
        type: "error",
        text:
          error.message ||
          "Pjesa nuk mund të ruhej.",
      });
      throw error;
    } finally {
      setSaving(false);
    }
  };
  /* =====================================================
     DELETE PART
  ===================================================== */
  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "A je e sigurt që do ta fshish këtë pjesë?"
    );
    if (!confirmed) {
      return;
    }
    const token = localStorage.getItem(
      "authToken"
    );
    if (!token) {
      setMessage({
        type: "error",
        text:
          "Sesioni ka përfunduar. Hyr përsëri.",
      });
      return;
    }
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/parts/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response
        .json()
        .catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          data?.details ||
          data?.error ||
          data?.message ||
          "Pjesa nuk mund të fshihej."
        );
      }
      setParts((previous) =>
        previous.filter(
          (part) =>
            Number(part.id) !==
            Number(id)
        )
      );
      setMessage({
        type: "success",
        text:
          "Pjesa u fshi me sukses.",
      });
      await fetchParts();
    } catch (error) {
      console.error(
        "DELETE PART ERROR:",
        error
      );
      setMessage({
        type: "error",
        text:
          error.message ||
          "Pjesa nuk mund të fshihej.",
      });
    }
  };
  /* =====================================================
     RESET FILTERS
  ===================================================== */
  const resetFilters = () => {
    setFilters(initialFilters);
    setCurrentPage(1);
  };
  /* =====================================================
     UI
  ===================================================== */
  return (
    <div className="parts-design min-h-screen overflow-x-hidden bg-gray-100 text-gray-800">
      <style>{partsDesign}</style>
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() =>
          setSidebarOpen(false)
        }
      />
      <button
        type="button"
        onClick={() =>
          setSidebarOpen(true)
        }
        className="
          fixed left-4 top-4 z-30
          flex h-11 w-11
          items-center justify-center
          rounded-xl bg-blue-700
          text-white shadow-lg
          transition hover:bg-blue-800
          active:scale-95
          lg:hidden
        "
        aria-label="Hap menunë"
      >
        <Menu size={24} />
      </button>
      <main
        className="
          min-h-screen min-w-0
          overflow-x-hidden
          px-4 pb-8 pt-20
          sm:px-6
          lg:ml-64
          lg:w-[calc(100%-16rem)]
          lg:p-8
        "
      >
        {/* HEADER */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600">
              Menaxhimi i pjesëve
            </span>
            <h1 className="mt-1 text-3xl font-bold text-gray-900">
              Pjesët
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Regjistro dhe menaxho pjesët
              e servisit.
            </p>
          </div>
          <button
            type="button"
            onClick={openAdd}
            className="
              w-full rounded-xl
              bg-blue-600 px-5 py-3
              font-semibold text-white
              shadow-md transition
              hover:bg-blue-700
              active:scale-[0.98]
              sm:w-auto
            "
          >
            + Shto pjesë
          </button>
        </div>
        {/* MESSAGE */}
        {message.text && (
          <div
            className={`
              mb-5 rounded-xl
              border px-4 py-3
              text-sm font-medium
              ${message.type ===
                "success"
                ? "border-green-200 bg-green-50 text-green-700"
                : "border-red-200 bg-red-50 text-red-700"
              }
            `}
          >
            {message.text}
          </div>
        )}
        {/* FILTERS */}
        <section className="mb-6 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-6">
            <div className="relative sm:col-span-2">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                value={filters.search}
                onChange={(event) =>
                  setFilters(
                    (previous) => ({
                      ...previous,
                      search:
                        event.target.value,
                    })
                  )
                }
                placeholder="Kërko pjesë, pajisje, brand..."
                className="
                  w-full rounded-xl
                  border border-gray-300
                  py-2.5 pl-10 pr-3
                  outline-none
                  focus:border-blue-500
                  focus:ring-4
                  focus:ring-blue-100
                "
              />
            </div>
            <select
              value={filters.category}
              onChange={(event) =>
                setFilters((previous) => ({
                  ...previous,
                  category:
                    event.target.value,
                }))
              }
              className="rounded-xl border border-gray-300 bg-white px-3 py-2.5"
            >
              <option value="All">
                Të gjitha kategoritë
              </option>
              {categories.map(
                (category) => (
                  <option
                    key={category}
                    value={category}
                  >
                    {category}
                  </option>
                )
              )}
            </select>
            <select
              value={filters.device}
              onChange={(event) =>
                setFilters((previous) => ({
                  ...previous,
                  device:
                    event.target.value,
                }))
              }
              className="rounded-xl border border-gray-300 bg-white px-3 py-2.5"
            >
              <option value="All">
                Të gjitha pajisjet
              </option>
              {devices.map((device) => (
                <option
                  key={device}
                  value={device}
                >
                  {device}
                </option>
              ))}
            </select>
            <select
              value={filters.brand}
              onChange={(event) =>
                setFilters((previous) => ({
                  ...previous,
                  brand:
                    event.target.value,
                }))
              }
              className="rounded-xl border border-gray-300 bg-white px-3 py-2.5"
            >
              <option value="All">
                Të gjitha brand-et
              </option>
              {brands.map((brand) => (
                <option
                  key={brand}
                  value={brand}
                >
                  {brand}
                </option>
              ))}
            </select>
            <select
              value={filters.stock}
              onChange={(event) =>
                setFilters((previous) => ({
                  ...previous,
                  stock:
                    event.target.value,
                }))
              }
              className="rounded-xl border border-gray-300 bg-white px-3 py-2.5"
            >
              <option value="All">
                Çdo stok
              </option>
              <option value="in">
                Në stok
              </option>
              <option value="out">
                Pa stok
              </option>
            </select>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <input
              type="number"
              min="0"
              placeholder="Çmimi minimal"
              value={filters.minPrice}
              onChange={(event) =>
                setFilters((previous) => ({
                  ...previous,
                  minPrice:
                    event.target.value,
                }))
              }
              className="rounded-xl border border-gray-300 px-3 py-2.5"
            />
            <input
              type="number"
              min="0"
              placeholder="Çmimi maksimal"
              value={filters.maxPrice}
              onChange={(event) =>
                setFilters((previous) => ({
                  ...previous,
                  maxPrice:
                    event.target.value,
                }))
              }
              className="rounded-xl border border-gray-300 px-3 py-2.5"
            />
            <button
              type="button"
              onClick={resetFilters}
              className="
                rounded-xl bg-gray-100
                px-4 py-2.5
                font-semibold text-gray-700
                transition hover:bg-gray-200
              "
            >
              Pastro filtrat
            </button>
          </div>
        </section>
        {/* RESULT INFO */}
        <div className="mb-3 flex items-center justify-between rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
          <span>
            <strong>
              {filteredParts.length}
            </strong>{" "}
            pjesë të gjetura
          </span>
          <span className="hidden sm:inline">
            Faqja {safePage} nga{" "}
            {totalPages}
          </span>
        </div>
        {/* TABLE */}
        <section className="hidden overflow-hidden rounded-2xl bg-white shadow-sm md:block">
          <div className="overflow-x-auto">
           <table className="w-full min-w-[1450px] text-sm">
              <thead className="bg-blue-600 text-white">
                <tr>
                  <th className="p-3 text-left">
                    ID
                  </th>
                  <th className="p-3 text-left">
                    Foto
                  </th>
                  <th className="p-3 text-left">
                    Emri
                  </th>
                  <th className="p-3 text-left">
                    Kategoria
                  </th>
                  <th className="p-3 text-left">
                    Pajisja
                  </th>
                  <th className="p-3 text-left">
                    Brand
                  </th>
                  <th className="p-3 text-left">
                    Model
                  </th>
                  <th className="p-3 text-left">
                    Stoku
                  </th>
                  <th className="whitespace-nowrap p-3 text-left">Çmim hyrje</th>
                  <th className="whitespace-nowrap p-3 text-left">Çmim shitje</th>
                  <th className="whitespace-nowrap p-3 text-left">Fitimi</th>
                  <th className="whitespace-nowrap p-3 text-left">Bonus %</th>
                  <th className="whitespace-nowrap p-3 text-left">Bonusi</th>
                  <th className="p-3 text-left">
                    Regjistruar nga
                  </th>
                  <th className="p-3 text-left">
                    Veprime
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="15"
                      className="px-4 py-16 text-center text-gray-500"
                    >
                      Duke ngarkuar pjesët...
                    </td>
                  </tr>
                ) : displayedParts.length >
                  0 ? (
                  displayedParts.map(
                    (part) => (
                      <tr
                        key={part.id}
                        className="border-b border-gray-100 hover:bg-blue-50/40"
                      >
                        <td className="p-3 font-medium text-gray-600">
                          #{part.id}
                        </td>
                        <td className="p-3">
                          {part.imageUrl ? (
                            <img
                              src={
                                part.imageUrl
                              }
                              alt={
                                part.name ||
                                "Pjesë"
                              }
                              className="h-14 w-16 rounded-xl border border-gray-100 object-cover"
                            />
                          ) : (
                            <div className="flex h-14 w-16 items-center justify-center rounded-xl bg-gray-100 text-gray-400">
                              <PackageSearch
                                size={22}
                              />
                            </div>
                          )}
                        </td>
                        <td className="max-w-[220px] p-3 font-bold text-gray-900">
                          <span className="block truncate">
                            {part.name ||
                              "Pa emër"}
                          </span>
                        </td>
                        <td className="p-3">
                          {part.category ||
                            "—"}
                        </td>
                        <td className="p-3">
                          {part.device ||
                            "—"}
                        </td>
                        <td className="p-3">
                          {part.brand ||
                            "—"}
                        </td>
                        <td className="p-3">
                          {part.model ||
                            "—"}
                        </td>
                        <td className="p-3">
                          <span
                            className={`
                              inline-flex
                              rounded-full
                              px-2.5 py-1
                              text-xs font-bold
                              ${Number(
                              part.stock
                            ) > 0
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                              }
                            `}
                          >
                            {Number(
                              part.stock || 0
                            )}
                          </span>
                        </td>
                        <td className="whitespace-nowrap p-3 font-semibold text-gray-700">
                          {formatPrice(part.purchase_price)} ALL
                        </td>
                        <td className="whitespace-nowrap p-3 font-bold text-green-600">
                          {formatPrice(part.price)} ALL
                        </td>
                        <td className="whitespace-nowrap p-3 font-bold text-blue-600">
                          {formatPrice(getPartProfit(part))} ALL
                        </td>
                        <td className="whitespace-nowrap p-3 font-bold text-violet-600">
                          {formatPrice(part.bonus_share_percent)}%
                        </td>
                        <td className="whitespace-nowrap p-3 font-bold text-violet-700">
                          {formatPrice(getPartBonus(part))} ALL
                        </td>
                        <td className="p-3">
                          {part.created_by_name ||
                            "—"}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                openEdit(part)
                              }
                              className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100"
                            >
                              <FaEdit />
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                handleDelete(
                                  part.id
                                )
                              }
                              className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-600 hover:bg-red-100"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  )
                ) : (
                  <tr>
                    <td
                      colSpan="15"
                      className="px-4 py-16 text-center text-gray-500"
                    >
                      <PackageSearch
                        size={42}
                        className="mx-auto mb-3 text-gray-300"
                      />
                      Nuk u gjet asnjë pjesë.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        {/* =====================================================
            MOBILE PART CARDS
            Desktop-i nuk preket.
        ===================================================== */}
        <section className="space-y-3 md:hidden">
          {loading ? (
            <div className="rounded-2xl bg-white px-4 py-10 text-center text-sm text-gray-500 shadow-sm">
              Duke ngarkuar pjesët...
            </div>
          ) : displayedParts.length > 0 ? (
            displayedParts.map((part) => (
              <article
                key={part.id}
                className="
                  overflow-hidden
                  rounded-2xl
                  border border-gray-200
                  bg-white
                  shadow-sm
                "
              >
                <button
                  type="button"
                  onClick={() =>
                    setMobileDetailPart(part)
                  }
                  className="
                    block w-full
                    p-4
                    text-left
                    transition
                    active:bg-gray-50
                  "
                >
                  <div className="flex items-start gap-3">
                    {part.imageUrl ? (
                      <img
                        src={part.imageUrl}
                        alt={part.name || "Pjesë"}
                        className="
                          h-20 w-20
                          shrink-0
                          rounded-2xl
                          border border-gray-100
                          object-cover
                        "
                      />
                    ) : (
                      <div
                        className="
                          flex h-20 w-20
                          shrink-0
                          items-center justify-center
                          rounded-2xl
                          bg-gray-100
                          text-gray-400
                        "
                      >
                        <PackageSearch size={30} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-[11px] font-bold uppercase tracking-wide text-blue-600">
                            #{part.id}
                          </p>
                          <h3
                            className="
                              mt-1
                              line-clamp-2
                              text-base
                              font-extrabold
                              leading-5
                              text-gray-900
                            "
                          >
                            {part.name || "Pa emër"}
                          </h3>
                        </div>
                        <span
                          className={`
                            shrink-0
                            rounded-full
                            px-2.5 py-1
                            text-[10px]
                            font-bold
                            ${Number(part.stock || 0) > 0
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-red-100 text-red-700"
                            }
                          `}
                        >
                          Stok: {Number(part.stock || 0)}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        {[part.brand, part.device]
                          .filter(Boolean)
                          .join(" · ") || "Pa specifika"}
                      </p>
                      {part.category && (
                        <span className="mt-2 inline-flex rounded-full bg-gray-100 px-2 py-1 text-[10px] font-semibold text-gray-600">
                          {part.category}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 flex items-end justify-between gap-3 border-t border-gray-100 pt-4">
                    <div>
                      <span className="block text-[11px] font-medium uppercase tracking-wide text-gray-400">
                        Çmimi
                      </span>
                      <strong className="mt-1 block text-lg font-extrabold text-emerald-600">
                        {formatPrice(part.price)} ALL
                      </strong>
                    </div>
                    <div className="text-right">
                      <span className="block text-[10px] font-medium uppercase tracking-wide text-gray-400">
                        Model
                      </span>
                      <strong className="mt-1 block max-w-[120px] truncate text-xs text-gray-700">
                        {part.model || "—"}
                      </strong>
                      <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-blue-600">
                        Shiko detajet
                        <Eye size={15} />
                      </div>
                    </div>
                  </div>
                </button>
              </article>
            ))
          ) : (
            <div className="rounded-2xl border border-gray-200 bg-white px-4 py-12 text-center shadow-sm">
              <PackageSearch
                size={42}
                className="mx-auto mb-3 text-gray-300"
              />
              <p className="font-semibold text-gray-700">
                Nuk u gjet asnjë pjesë.
              </p>
              <p className="mt-1 text-xs text-gray-400">
                Ndrysho filtrat ose shto një pjesë të re.
              </p>
            </div>
          )}
        </section>
        {/* =====================================================
            MOBILE PART DETAIL MODAL
        ===================================================== */}
        {mobileDetailPart && (
          <div
            className="
              fixed inset-0 z-[9998]
              flex items-end justify-center
              bg-black/55
              backdrop-blur-sm
              sm:items-center
              sm:p-4
              md:hidden
            "
            onClick={() =>
              setMobileDetailPart(null)
            }
          >
            <div
              className="
                flex
                max-h-[94dvh]
                w-full
                flex-col
                overflow-hidden
                rounded-t-3xl
                bg-white
                shadow-2xl
                sm:max-w-lg
                sm:rounded-3xl
              "
              onClick={(event) =>
                event.stopPropagation()
              }
            >
              {/* HEADER */}
              <div
                className="
                  flex items-start justify-between
                  gap-4
                  border-b border-gray-100
                  bg-gradient-to-r
                  from-blue-700
                  to-indigo-700
                  px-5 py-5
                  text-white
                "
              >
                <div className="min-w-0">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-blue-100">
                    Detajet e pjesës
                  </span>
                  <h2 className="mt-1 line-clamp-2 text-xl font-extrabold">
                    {mobileDetailPart.name ||
                      "Pjesë"}
                  </h2>
                  <p className="mt-1 text-xs text-blue-100">
                    #{mobileDetailPart.id}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setMobileDetailPart(null)
                  }
                  className="
                    flex h-9 w-9
                    shrink-0
                    items-center justify-center
                    rounded-full
                    bg-white/10
                    transition
                    hover:bg-white/20
                  "
                  aria-label="Mbyll"
                >
                  <X size={19} />
                </button>
              </div>
              {/* CONTENT */}
              <div className="min-h-0 flex-1 overflow-y-auto p-5">
                <div className="flex items-start gap-4">
                  {mobileDetailPart.imageUrl ? (
                    <img
                      src={
                        mobileDetailPart.imageUrl
                      }
                      alt={
                        mobileDetailPart.name ||
                        "Pjesë"
                      }
                      className="
                        h-28 w-28
                        shrink-0
                        rounded-2xl
                        border border-gray-100
                        object-cover
                      "
                    />
                  ) : (
                    <div
                      className="
                        flex h-28 w-28
                        shrink-0
                        items-center justify-center
                        rounded-2xl
                        bg-gray-100
                        text-gray-400
                      "
                    >
                      <PackageSearch size={38} />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-400">
                      Çmimi
                    </p>
                    <p className="mt-1 text-2xl font-black text-emerald-600">
                      {formatPrice(
                        mobileDetailPart.price
                      )}{" "}
                      ALL
                    </p>
                    <div className="mt-3">
                      <span
                        className={`
                          inline-flex
                          rounded-full
                          px-2.5 py-1
                          text-[10px]
                          font-bold
                          ${Number(
                          mobileDetailPart.stock || 0
                        ) > 0
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-red-100 text-red-700"
                          }
                        `}
                      >
                        Stok:{" "}
                        {Number(
                          mobileDetailPart.stock || 0
                        )}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  {[
                    ["Kategoria", mobileDetailPart.category],
                    ["Pajisja", mobileDetailPart.device],
                    ["Brand", mobileDetailPart.brand],
                    ["Model", mobileDetailPart.model],
                    [
                      "Regjistruar nga",
                      mobileDetailPart.created_by_name,
                    ],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="rounded-2xl bg-gray-50 p-3"
                    >
                      <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                        {label}
                      </span>
                      <strong className="mt-1 block break-words text-sm text-gray-800">
                        {value || "—"}
                      </strong>
                    </div>
                  ))}
                </div>
                {mobileDetailPart.description && (
                  <section className="mt-5 rounded-2xl border border-gray-200 p-4">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                      Përshkrimi
                    </span>
                    <p className="mt-2 text-sm leading-6 text-gray-700">
                      {mobileDetailPart.description}
                    </p>
                  </section>
                )}
              </div>
              {/* ACTIONS */}
              <div className="grid grid-cols-2 gap-2 border-t border-gray-200 bg-white p-3">
                <button
                  type="button"
                  onClick={() => {
                    const part =
                      mobileDetailPart;
                    setMobileDetailPart(null);
                    window.setTimeout(() => {
                      openEdit(part);
                    }, 0);
                  }}
                  className="
                    flex items-center justify-center
                    gap-2
                    rounded-xl
                    bg-blue-50
                    px-4 py-3
                    text-sm font-bold
                    text-blue-700
                    transition
                    active:scale-[0.98]
                  "
                >
                  <FaEdit />
                  Modifiko
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const id =
                      mobileDetailPart.id;
                    setMobileDetailPart(null);
                    handleDelete(id);
                  }}
                  className="
                    flex items-center justify-center
                    gap-2
                    rounded-xl
                    bg-red-50
                    px-4 py-3
                    text-sm font-bold
                    text-red-700
                    transition
                    active:scale-[0.98]
                  "
                >
                  <FaTrash />
                  Fshi
                </button>
              </div>
            </div>
          </div>
        )}
        {/* PAGINATION */}
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            type="button"
            disabled={safePage === 1}
            onClick={() =>
              goToPage(safePage - 1)
            }
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 disabled:opacity-40"
          >
            ←
          </button>
          <span className="rounded-lg bg-white px-4 py-2 text-sm font-semibold shadow-sm">
            {safePage} / {totalPages}
          </span>
          <button
            type="button"
            disabled={
              safePage === totalPages
            }
            onClick={() =>
              goToPage(safePage + 1)
            }
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 disabled:opacity-40"
          >
            →
          </button>
        </div>
        {/* MODAL */}
        <dialog
          ref={dialogRef}
          onCancel={(event) => {
            event.preventDefault();
            closeDialog();
          }}
          className="
            m-auto
            max-h-[94dvh]
            w-[calc(100%-1rem)]
            max-w-[760px]
            overflow-y-auto
            rounded-2xl
            bg-white p-0
            shadow-2xl
            backdrop:bg-black/50
            backdrop:backdrop-blur-sm
          "
        >
          <div className="sticky top-0 z-20 flex items-center justify-between border-b border-gray-200 bg-white px-5 py-4">
            <div>
              <span className="text-xs font-bold uppercase tracking-wide text-blue-600">
                {editingPart
                  ? "Përditësim"
                  : "Regjistrim i ri"}
              </span>
              <h2 className="mt-1 text-lg font-bold text-gray-900">
                {editingPart
                  ? "Modifiko pjesën"
                  : "Shto pjesë"}
              </h2>
            </div>
            <button
              type="button"
              onClick={closeDialog}
              disabled={saving}
              className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-red-600"
            >
              ✕
            </button>
          </div>
          <AddPartForm
            ref={formRef}
            onAdd={handleSavePart}
            initialData={editingPart}
            parts={parts}
          />
        </dialog>
      </main>
    </div>
  );
};
export default AdminParts;
const partsDesign = "/* Vetëm pamja e AdminParts. Nuk ndryshon fusha ose funksione. */\n.parts-design{background:#f9fafb;color:#111827;font-family:inherit}\n.parts-design main{font-size:14px;line-height:1.5;color:#374151}\n.parts-design main button,.parts-design main input,.parts-design main select,.parts-design main textarea{font-family:inherit;font-size:14px;line-height:1.5;letter-spacing:normal}\n.parts-design main>.mb-6.flex{background:transparent;padding:0;border:0;box-shadow:none}\n.parts-design main h1{font-size:30px;line-height:1.25;font-weight:700;letter-spacing:normal;color:#111827}\n.parts-design main>.mb-6.flex p{font-size:14px;color:#6b7280}\n.parts-design main>.mb-6.flex button{border-radius:12px;background:#2563eb;box-shadow:none;padding:12px 20px;font-weight:600}\n.parts-design main>.mb-6.flex button:hover{background:#1d4ed8}\n.parts-design main>section.border{border-color:#e5e7eb;border-radius:16px;padding:22px 24px;box-shadow:none}\n.parts-design main input,.parts-design main select{border-color:#d1d5db;border-radius:10px;background:#fff;color:#374151;min-height:44px}\n.parts-design main input:focus,.parts-design main select:focus{outline:2px solid #bfdbfe;outline-offset:1px}\n.parts-design main>.border-blue-100{border-color:#e5e7eb;background:#fff;color:#6b7280;border-radius:12px;font-size:14px}\n.parts-design main>section.hidden{border:1px solid #e5e7eb;border-radius:16px;box-shadow:none}\n.parts-design main table{font-size:14px;line-height:1.5}\n.parts-design main thead{background:#f9fafb;color:#6b7280;border-bottom:1px solid #e5e7eb}\n.parts-design main th{padding:16px 20px;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.4px}\n.parts-design main td{padding:18px 20px;font-size:14px;border-color:#f3f4f6}\n.parts-design main tbody tr:hover{background:#f9fafb}\n.parts-design main td.font-bold,.parts-design main td.font-semibold{font-weight:600}\n.parts-design main td span.rounded-full{font-size:12px;font-weight:600;padding:6px 11px}\n.parts-design main td button{width:36px;height:36px;border:1px solid #e5e7eb;border-radius:8px}\n.parts-design main .shadow-sm{box-shadow:none}\n.parts-design main .font-extrabold,.parts-design main .font-black{font-weight:700}\n.parts-design main>.mt-6.flex{gap:10px}\n.parts-design main>.mt-6.flex>button{width:42px;height:42px;padding:0;background:#fff;border:1px solid #e5e7eb;border-radius:9px;color:#374151}\n.parts-design main>.mt-6.flex>span{display:flex;align-items:center;justify-content:center;min-width:90px;height:42px;background:#fff;border:1px solid #e5e7eb;border-radius:9px;font-size:14px;font-weight:500;box-shadow:none}\n.parts-design main dialog{border:1px solid #e5e7eb;border-radius:16px;color:#111827;font-family:inherit;box-shadow:0 30px 100px #182c5040}\n.parts-design main dialog::backdrop{background:#15243a88;backdrop-filter:blur(4px)}\n.parts-design main dialog h2{font-size:22px;font-weight:700;letter-spacing:normal}\n.parts-design main dialog label{font-size:14px;font-weight:500}\n.parts-design main dialog input,.parts-design main dialog textarea,.parts-design main dialog select{font-size:14px;border:1px solid #d1d5db;border-radius:10px}\n.parts-design main .bg-gradient-to-r.from-blue-700{background:#f9fafb;color:#111827;border-color:#e5e7eb}\n.parts-design main .bg-gradient-to-r.from-blue-700 .text-blue-100{color:#6b7280}\n.parts-design main .bg-gradient-to-r.from-blue-700 button{background:#fff;color:#6b7280;border:1px solid #e5e7eb}\n.parts-design main .bg-gradient-to-r.from-blue-700 h2{font-size:20px;font-weight:700}\n.parts-design main .md\\:hidden article{border-color:#e5e7eb;border-radius:16px;box-shadow:none}\n.parts-design main .md\\:hidden h3{font-size:16px;font-weight:600;line-height:1.5}\n.parts-design main button:focus-visible{outline:2px solid #5385ef;outline-offset:3px}\n@media(max-width:767px){.parts-design main h1{font-size:26px}.parts-design main>section.border{padding:18px 16px}.parts-design main>.mb-6.flex p{font-size:13px}}\n";